import moment from 'moment-timezone';

export const canManageQuickBooks = (permissions = {}) => !!(permissions.owner || permissions.admin);
export const isQuickBooksEnvironment = (environment) => ['Production', 'Sandbox'].includes(environment);
export const isReady = (connection) => connection?.environment === 'Production' && connection.oauthAuthorized === true && connection.status === 'Connected'
  && connection.invoiceSyncEnabled === true && connection.invoiceSyncMode === 'Manual';
export const customerName = (customer) => [customer?.firstName, customer?.lastName].filter(Boolean).join(' ')
  || customer?.email || customer?.id || 'Unknown customer';

export const money = (amount, currency = 'USD') => {
  if (amount == null || !Number.isFinite(Number(amount))) return 'Unavailable';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch (_error) { return `${amount} ${currency}`; }
};

export const errorMessage = (error) => error?.data?.message || error?.response?.data?.message || error?.message || 'Request failed. Please retry.';
export const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'AbortError';
export const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

export const newRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; // eslint-disable-line no-bitwise
  bytes[8] = (bytes[8] & 63) | 128; // eslint-disable-line no-bitwise
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const dateRange = (start, end, timezone) => {
  if (!moment.tz.zone(timezone)) throw new Error('Select a valid timezone.');
  const from = moment.tz(start, 'YYYY-MM-DD', true, timezone);
  const to = moment.tz(end, 'YYYY-MM-DD', true, timezone);
  if (!from.isValid() || !to.isValid() || to.isBefore(from)) throw new Error('Enter a valid start and end date.');
  return {
    startDate: from.startOf('day').toISOString(),
    endDate: to.add(1, 'day').startOf('day').toISOString(),
  };
};

export const ineligibleReason = (billable, customerWorkspace = false) => {
  if (billable.quickBooksExportId) return 'Reserved for QuickBooks; view the existing export';
  if (billable.status === 'Paid') return 'Already paid; cannot export';
  if (billable.status === 'Void') return 'Voided; cannot export';
  if (billable.status === 'Expense' || billable.receivable === false) return 'Expenses cannot be exported as customer charges';
  if (billable.status !== 'Open' || billable.receivable !== true) return 'Not an open receivable';
  if (Number(billable.price) <= 0 || !Number.isFinite(Number(billable.price))) return 'Positive amount required';
  if (!billable.billableDate) return 'Billing date missing';
  if (!customerWorkspace && !billable.barnUsers?.length) return 'No billing customer assigned';
  return '';
};

export const validatePricing = (line, request) => {
  const positive = (value) => value != null && Number.isFinite(Number(value)) && Number(value) > 0;
  const selection = request.billables.find((item) => item.billableId === line.billableId);
  if (!['QuestriTransaction', 'QuickBooksService'].includes(line.priceSource)
    || !['AssignedUser', 'HorseAccess'].includes(line.billingBasis)
    || (line.billingBasis === 'HorseAccess' && selection?.useHorseAccess !== true)
    || ![line.sourceAmount, line.unitPrice, line.exportQuantity].every(positive)
    || Math.abs(Math.round((Number(line.unitPrice) * Number(line.exportQuantity) + Number.EPSILON) * 100) / 100 - Number(line.amount)) > 0.001
    || (line.priceSource === 'QuestriTransaction'
      && (Number(line.exportQuantity) !== 1 || Number(line.amount) !== Number(line.sourceAmount)))) {
    throw new Error('The export pricing or billing customer is incomplete. Review a new preview.');
  }
};

export const validatePreview = (preview, request, connection) => {
  const expected = new Map(request.billables.map((line) => [line.billableId, line.billingCustomerId]));
  const seen = new Set();
  if (![1, 2, 3, 4].includes(preview?.schemaVersion) || !/^[a-f0-9]{64}$/.test(preview?.previewHash || '')
    || !uuidPattern.test(preview?.connectionId || '') || !isQuickBooksEnvironment(preview.environment)
    || !Array.isArray(preview.customers) || preview.customers.length === 0 || !preview.realmId
    || !Number.isFinite(Number(preview.totalAmount)) || Number(preview.totalAmount) <= 0
    || preview.currencyCode !== request.currencyCode || preview.invoiceDate !== request.invoiceDate
    || (preview.dueDate || null) !== (request.dueDate || null) || preview.timezone !== request.timezone) {
    throw new Error('The export preview is incomplete or incompatible. Refresh and review again.');
  }
  if (!connection || preview.connectionId !== connection.id || preview.realmId !== connection.realmId
    || preview.environment !== connection.environment) {
    throw new Error('The QuickBooks company or environment changed. Refresh setup and review again.');
  }
  preview.customers.forEach((customer) => {
    const target = request.existingInvoices?.[customer.billingCustomerId];
    const existing = customer.existingInvoice;
    const validMoney = (value) => value != null && Number.isFinite(Number(value)) && Number(value) >= 0;
    const taxTotalsValid = existing && [existing.subtotalAmount, existing.taxAmount, existing.resultingSubtotal].every(validMoney)
      && Math.abs(Number(existing.subtotalAmount) + Number(existing.taxAmount) - Number(existing.totalAmount)) < 0.001
      && Math.abs(Number(existing.resultingSubtotal) - Number(existing.subtotalAmount) - Number(customer.totalAmount)) < 0.001;
    const legacyTotalValid = existing && validMoney(existing.resultingTotal)
      && Math.abs(Number(existing.resultingTotal) - Number(existing.totalAmount) - Number(customer.totalAmount)) < 0.001;
    let totalsValid = legacyTotalValid;
    if (preview.schemaVersion >= 3) totalsValid = taxTotalsValid;
    if ((target || null) !== (existing?.id || null)
      || (target && (preview.schemaVersion < 2 || !existing.syncToken || !Array.isArray(existing.lines)
        || !existing.invoiceDate || !Number.isFinite(Number(existing.totalAmount)) || Number(existing.totalAmount) <= 0
        || Number(existing.balance) !== Number(existing.totalAmount)
        || !totalsValid))) {
      throw new Error('The invoice destination changed or is incomplete. Review a new preview.');
    }
    if (!customer.remoteCustomerId || !Array.isArray(customer.lines) || customer.lines.length === 0
      || !Number.isFinite(Number(customer.totalAmount)) || Number(customer.totalAmount) <= 0) {
      throw new Error('The export preview is missing customer or transaction details.');
    }
    customer.lines.forEach((line) => {
      if (expected.get(line.billableId) !== customer.billingCustomerId || seen.has(line.billableId)
        || !line.remoteItemId || !Number.isFinite(Number(line.amount)) || Number(line.amount) <= 0) {
        throw new Error('The export preview does not match the selected transactions.');
      }
      if (preview.schemaVersion >= 4) validatePricing(line, request);
      seen.add(line.billableId);
    });
  });
  if (seen.size !== expected.size) throw new Error('The export preview is missing selected transactions.');
  return preview;
};

export const exportStatus = (lines = []) => {
  const statuses = new Set(lines.map((line) => line.status));
  if (!statuses.size) return { label: 'Status unavailable' };
  const known = ['Pending', 'Processing', 'Succeeded', 'Failed', 'DeadLetter', 'Skipped'];
  if (lines.some((line) => !known.includes(line.status))) return { label: 'Status unavailable' };
  if (statuses.size === 1 && statuses.has('Succeeded')) {
    return {
      label: 'Synced',
      color: 'success',
    };
  }
  if (statuses.has('Succeeded')) {
    return {
      label: 'Partially synced',
      color: 'warning',
    };
  }
  if (statuses.has('Failed') || statuses.has('DeadLetter')) {
    return {
      label: 'Needs attention',
      color: 'error',
    };
  }
  if (statuses.has('Processing')) {
    return {
      label: 'Processing',
      color: 'info',
    };
  }
  if (statuses.has('Skipped')) {
    return {
      label: 'Skipped - reserved',
      color: 'warning',
    };
  }
  return {
    label: lines.some((line) => line.deliveryRequested)
      ? 'Waiting to send'
      : 'Approved - not sent',
  };
};

const pendingKey = (tenant) => `questri:quickbooks:pending:${tenant}`;

export const readPendingApproval = (tenant) => {
  const raw = window.sessionStorage.getItem(pendingKey(tenant));
  if (!raw) return null;
  const pending = JSON.parse(raw);
  if (pending.version !== 1 || pending.tenant !== tenant || !isQuickBooksEnvironment(pending.environment)
    || !uuidPattern.test(pending.connectionId || '') || !pending.realmId
    || !uuidPattern.test(pending.request?.requestId || '') || !/^[a-f0-9]{64}$/.test(pending.request?.expectedPreviewHash || '')
    || !Array.isArray(pending.request?.billables) || pending.request.billables.length === 0) {
    throw new Error('The saved approval cannot be recovered. Contact support before creating another export.');
  }
  return pending;
};

export const savePendingApproval = (tenant, request, preview) => {
  const pending = {
    version: 1,
    tenant,
    environment: preview.environment,
    connectionId: preview.connectionId,
    realmId: preview.realmId,
    request,
  };
  window.sessionStorage.setItem(pendingKey(tenant), JSON.stringify(pending));
  return pending;
};

export const clearPendingApproval = (tenant) => window.sessionStorage.removeItem(pendingKey(tenant));

export const definitelyRejected = (error) => {
  const status = error?.status || error?.response?.status;
  const message = errorMessage(error);
  return status === 400 && (
    message.startsWith('Billables or QuickBooks settings changed.')
    || message === 'A selected billable is already reserved for a QuickBooks export.'
  );
};
